import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MapPin,
  Send,
  ThumbsUp,
  Construction,
  ShieldAlert,
  ArrowLeftRight,
  Droplets,
  Snowflake,
  Circle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_TYPES = [
  { value: 'pothole', label: 'Schlagloch', icon: '\u{1F573}\uFE0F', lucide: Circle },
  { value: 'construction', label: 'Baustelle', icon: '\u{1F6A7}', lucide: Construction },
  { value: 'surface_damage', label: 'Fahrbahnschaden', icon: '\u26A0\uFE0F', lucide: ShieldAlert },
  { value: 'narrow', label: 'Engstelle', icon: '\u2194\uFE0F', lucide: ArrowLeftRight },
  { value: 'flooded', label: '\u00DCberflutet', icon: '\u{1F30A}', lucide: Droplets },
  { value: 'ice', label: 'Gl\u00E4tte', icon: '\u2744\uFE0F', lucide: Snowflake },
];

const SEVERITY_CONFIG = {
  low: { label: 'Gering', color: 'bg-green-500/10 text-green-500 border-green-500/30' },
  medium: { label: 'Mittel', color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  high: { label: 'Hoch', color: 'bg-red-500/10 text-red-500 border-red-500/30' },
};

const getConditionType = (value) => CONDITION_TYPES.find((c) => c.value === value);

const RoadConditions = ({ routeCoordinates, onReportLocation }) => {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [activeTab, setActiveTab] = useState('along');

  const [formData, setFormData] = useState({
    type: 'pothole',
    severity: 'medium',
    description: '',
    latitude: null,
    longitude: null,
  });

  const fetchConditions = useCallback(async () => {
    if (!routeCoordinates || routeCoordinates.length === 0) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/road-conditions/along-route`, {
        params: {
          coordinates: JSON.stringify(routeCoordinates),
        },
      });
      setConditions(Array.isArray(response.data) ? response.data : response.data.conditions || []);
    } catch (error) {
      console.error('Failed to fetch road conditions:', error);
    } finally {
      setLoading(false);
    }
  }, [routeCoordinates]);

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  useEffect(() => {
    // Attempt to get current position for the report form
    if (activeTab === 'report' && !formData.latitude) {
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          setFormData((prev) => ({
            ...prev,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }));
        },
        () => {
          // Silently fail - user can still submit without coords
        }
      );
    }
  }, [activeTab, formData.latitude]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      toast.error('Bitte eine Beschreibung eingeben');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        type: formData.type,
        severity: formData.severity,
        description: formData.description.trim(),
        latitude: formData.latitude,
        longitude: formData.longitude,
      };
      const response = await axios.post(`${API}/road-conditions`, payload);
      setConditions((prev) => [response.data, ...prev]);
      setFormData({ type: 'pothole', severity: 'medium', description: '', latitude: formData.latitude, longitude: formData.longitude });
      toast.success('Meldung gesendet');
      if (onReportLocation && payload.latitude && payload.longitude) {
        onReportLocation({ lat: payload.latitude, lng: payload.longitude });
      }
    } catch (error) {
      console.error('Failed to report condition:', error);
      toast.error('Meldung konnte nicht gesendet werden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (id) => {
    try {
      setVerifying(id);
      const response = await axios.post(`${API}/road-conditions/${id}/verify`);
      setConditions((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, verified_count: response.data.verified_count ?? (c.verified_count || 0) + 1 } : c
        )
      );
      toast.success('Best\u00E4tigt');
    } catch (error) {
      console.error('Failed to verify condition:', error);
      toast.error('Best\u00E4tigung fehlgeschlagen');
    } finally {
      setVerifying(null);
    }
  };

  return (
    <div className="h-full bg-background">
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-primary" />
            Stra\u00DFenzustand
          </h1>
          <p className="text-muted-foreground mt-2">
            Aktuelle Meldungen und Hinweise der Community
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'along' ? 'default' : 'outline'}
            className={activeTab === 'along'
              ? 'bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground'
            }
            onClick={() => setActiveTab('along')}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Entlang der Route
          </Button>
          <Button
            variant={activeTab === 'report' ? 'default' : 'outline'}
            className={activeTab === 'report'
              ? 'bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground'
            }
            onClick={() => setActiveTab('report')}
          >
            <Send className="w-4 h-4 mr-2" />
            Melden
          </Button>
        </div>

        {/* Along Route Tab */}
        {activeTab === 'along' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : conditions.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Alles frei!
                  </h3>
                  <p className="text-muted-foreground">
                    Keine gemeldeten Probleme entlang deiner Route.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[calc(100vh-320px)]">
                <div className="space-y-4 pr-4">
                  {conditions.map((condition) => {
                    const typeConfig = getConditionType(condition.type);
                    const severityConfig = SEVERITY_CONFIG[condition.severity] || SEVERITY_CONFIG.medium;

                    return (
                      <Card key={condition.id} className="bg-card border-border hover:border-primary/20 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Type Icon */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-lg">
                              {typeConfig?.icon || '\u26A0\uFE0F'}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-medium text-foreground text-sm">
                                  {typeConfig?.label || condition.type}
                                </span>
                                <Badge variant="outline" className={severityConfig.color}>
                                  {severityConfig.label}
                                </Badge>
                              </div>

                              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                {condition.description}
                              </p>

                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <ThumbsUp className="w-3 h-3" />
                                  <span>
                                    {condition.verified_count || 0}x best\u00E4tigt
                                  </span>
                                </div>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs border-border text-muted-foreground hover:text-primary hover:border-primary/30 active:scale-95 transition-all"
                                  onClick={() => handleVerify(condition.id)}
                                  disabled={verifying === condition.id}
                                >
                                  {verifying === condition.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <ThumbsUp className="w-3 h-3 mr-1" />
                                  )}
                                  Best\u00E4tigen
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display text-lg text-foreground">
                Neue Meldung
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Condition Type */}
                <div className="space-y-2">
                  <Label className="text-foreground text-sm">Art der Beeintr\u00E4chtigung</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(val) => setFormData({ ...formData, type: val })}
                  >
                    <SelectTrigger className="bg-muted/50 border-border h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {CONDITION_TYPES.map((ct) => (
                        <SelectItem key={ct.value} value={ct.value}>
                          {ct.icon} {ct.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Severity */}
                <div className="space-y-2">
                  <Label className="text-foreground text-sm">Schweregrad</Label>
                  <div className="flex gap-2">
                    {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                      <Badge
                        key={key}
                        variant="outline"
                        className={`cursor-pointer flex-1 justify-center py-2 transition-all active:scale-95 ${
                          formData.severity === key
                            ? config.color
                            : 'border-border text-muted-foreground hover:border-foreground/30'
                        }`}
                        onClick={() => setFormData({ ...formData, severity: key })}
                      >
                        {config.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-foreground text-sm">Beschreibung</Label>
                  <Textarea
                    placeholder="Beschreibe das Problem..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-muted/50 border-border min-h-[100px]"
                    required
                  />
                </div>

                {/* Coordinates Display */}
                <div className="space-y-2">
                  <Label className="text-foreground text-sm">Koordinaten</Label>
                  <div className="flex items-center gap-2 p-3 rounded-md bg-muted/30 border border-border">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    {formData.latitude && formData.longitude ? (
                      <span className="font-mono text-sm text-foreground">
                        {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Position wird ermittelt...
                      </span>
                    )}
                  </div>
                </div>

                <Separator className="bg-border" />

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg active:scale-95 transition-all h-12"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Meldung senden
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RoadConditions;
